
import type { ReactNode } from "react";
import NativeCallPage from './pages/NativeCallPage';
import CallEndedPage from './pages/CallEndedPage';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Navigate,
  Routes,
  Route,
} from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { MembershipProvider } from "@/contexts/MembershipContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import ResetPassword from "./pages/ResetPassword";
import SmtpSettings from "./pages/SmtpSettings";
import Account from "./pages/Account";
import PrivacySafety from "./pages/PrivacySafety";
import Terms from "./pages/Terms";
import InviteLanding from "./pages/InviteLanding";
import Join from "./pages/Join";
import Signup from "./pages/Signup";
import AuthCallback from "./pages/AuthCallback";
import Login from "./pages/Login";
import SystemHealth from "./pages/SystemHealth";
import GraphIntelligence from "./pages/GraphIntelligence";
import { FamilyGraphProvider } from "./contexts/FamilyGraphContext";
import AITestSandbox from "./pages/AITestSandbox";
import AISemanticLab from "./pages/AISemanticLab";
import LegacyViewer from "./pages/LegacyViewer";
import LegacyPreservationViewer from "./pages/LegacyPreservationViewer";
import SecureKeyVault from "./pages/SecureKeyVault";
import AdminIntegrity from "./pages/AdminIntegrity";
import GovernorCommandCenter from "./pages/GovernorCommandCenter";
import RouteRecoveryBoundary from "@/components/system/RouteRecoveryBoundary";
import GovernorStartupBridge from './components/governor/GovernorStartupBridge';
import RouteAuthorityRuntimeBridge from './components/governor/RouteAuthorityRuntimeBridge';
import RouteAccessBoundary from './components/routing/RouteAccessBoundary';


const queryClient = new QueryClient();


// ─────────────────────────────────────────────────────────────────────────────
// Production stability charter §7.1 — Prevent blank pages.
//
// Every route below is wrapped in a <RouteErrorBoundary> so a render error
// in any single page (Account, Login, ResetPassword, Privacy, Terms, etc.)
// falls back to the universal "We're reconnecting your family space" recovery
// surface instead of a blank white/black screen.
//
// The dashboard ("/" → <Index/>) has its OWN top-level <AppBootErrorBoundary>
// inside src/pages/Index.tsx that wraps AppLayout, so it is doubly protected.
// All boundaries render <AppRecovery /> with a working "Retry connection"
// button.
// ─────────────────────────────────────────────────────────────────────────────
const wrap = (label: string, node: ReactNode) => (
  <RouteErrorBoundary label={label}>{node}</RouteErrorBoundary>
);

// Phase 7A — nested RouteRecoveryBoundary wrap for high-risk admin /
// playback routes. When the `routeRecovery` flag is OFF (the default in
// production), this is an identity passthrough — the existing
// RouteErrorBoundary still owns top-level isolation. When ON, the inner
// boundary catches subtree-only errors with a friendlier fallback while
// the outer boundary remains the safety net.
const wrapHardened = (label: string, scope: string, node: ReactNode) => (
  <RouteErrorBoundary label={label}>
    <RouteRecoveryBoundary scope={scope} label={label}>
      {node}
    </RouteRecoveryBoundary>
  </RouteErrorBoundary>
);


const App = () => (
  <ThemeProvider defaultTheme="light">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <LanguageProvider>
            <MembershipProvider>
              <GovernorStartupBridge />
              <RouteAuthorityRuntimeBridge />
      <Routes>
        <Route
          path="/governor"
          element={
            <RouteAccessBoundary level="command-center">
              <Navigate to="/governor-command-center" replace />
            </RouteAccessBoundary>
          }
        />
          <Route
            path="/native-call"
            element={
              <RouteAccessBoundary level="authenticated">
                <NativeCallPage />
              </RouteAccessBoundary>
            }
          />
          <Route path="/call-ended" element={<CallEndedPage />} />
                <Route path="/" element={wrap('PublicLanding', <Index />)} />
            <Route
              path="/app"
              element={
                <RouteAccessBoundary level="authenticated">
                  {wrap('MemberApplication', <Index />)}
                </RouteAccessBoundary>
              }
            />
            <Route
              path="/dashboard"
              element={
                <RouteAccessBoundary level="authenticated">
                  <Navigate to="/app" replace />
                </RouteAccessBoundary>
              }
            />
                <Route path="/reset-password" element={wrap('ResetPassword', <ResetPassword />)} />
                <Route
                  path="/settings/email"
                  element={
                    <RouteAccessBoundary level="staff">
                      {wrap('SmtpSettings', <SmtpSettings />)}
                    </RouteAccessBoundary>
                  }
                />
                <Route
                  path="/account"
                  element={
                    <RouteAccessBoundary level="authenticated">
                      <Navigate to="/settings" replace />
                    </RouteAccessBoundary>
                  }
                />
                <Route
                  path="/membership"
                  element={
                    <RouteAccessBoundary level="authenticated">
                      <Navigate to="/settings" replace />
                    </RouteAccessBoundary>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <RouteAccessBoundary level="authenticated">
                      {wrap('Settings', <Account />)}
                    </RouteAccessBoundary>
                  }
                />
                <Route path="/privacy" element={wrap('Privacy', <PrivacySafety />)} />
                <Route path="/terms" element={wrap('Terms', <Terms />)} />
                <Route path="/invite" element={wrap('Invite', <Join />)} />
                <Route path="/invite/:inviterId" element={wrap('InviteLanding', <InviteLanding />)} />
                <Route path="/join" element={wrap('Join', <Join />)} />
                <Route path="/join/:inviterId" element={wrap('JoinLanding', <InviteLanding />)} />

                <Route path="/auth/callback" element={wrap('AuthCallback', <AuthCallback />)} />
                <Route path="/login" element={wrap('Login', <Login />)} />
                
          <Route path="/log-in" element={wrap('LogIn', <Login />)} /><Route path="/signin" element={wrap('SignIn', <Login />)} />
          <Route path="/signup" element={wrap('Signup', <Signup />)} />
                <Route
                  path="/admin/health"
                  element={
                    <RouteAccessBoundary level="command-center">
                      {wrap('SystemHealth', <SystemHealth />)}
                    </RouteAccessBoundary>
                  }
                />
                <Route
                  path="/system-health"
                  element={
                    <RouteAccessBoundary level="command-center">
                      {wrap('SystemHealth', <SystemHealth />)}
                    </RouteAccessBoundary>
                  }
                />

<Route
  path="/graph-intelligence"
  element={
    <RouteAccessBoundary level="authenticated">
      {wrap(
        'GraphIntelligence',
        <FamilyGraphProvider>
          <GraphIntelligence />
        </FamilyGraphProvider>,
      )}
    </RouteAccessBoundary>
  }
/>
                <Route
                  path="/admin/ai"
                  element={
                    <RouteAccessBoundary level="staff">
                      {wrapHardened('AITestSandbox', 'admin/ai', <AITestSandbox />)}
                    </RouteAccessBoundary>
                  }
                />
                <Route
                  path="/admin/semantic"
                  element={
                    <RouteAccessBoundary level="staff">
                      {wrapHardened('AISemanticLab', 'admin/semantic', <AISemanticLab />)}
                    </RouteAccessBoundary>
                  }
                />
                <Route
                  path="/admin/legacy"
                  element={
                    <RouteAccessBoundary level="staff">
                      {wrapHardened('LegacyViewer', 'admin/legacy', <LegacyViewer />)}
                    </RouteAccessBoundary>
                  }
                />
                <Route
                  path="/admin/preservation"
                  element={
                    <RouteAccessBoundary level="staff">
                      {wrapHardened(
                        'LegacyPreservationViewer',
                        'admin/preservation',
                        <LegacyPreservationViewer />,
                      )}
                    </RouteAccessBoundary>
                  }
                />

                <Route
  path="/admin/integrity"
  element={
    <RouteAccessBoundary level="staff">
      {wrapHardened(
        'AdminIntegrity',
        'admin/integrity',
        <AdminIntegrity />,
      )}
    </RouteAccessBoundary>
  }
/>
          <Route
            path="/admin/keys"
            element={
              <RouteAccessBoundary level="staff">
                {wrap('SecureKeyVault', <SecureKeyVault />)}
              </RouteAccessBoundary>
            }
          />
                <Route
                  path="/secure-keys"
                  element={
                    <RouteAccessBoundary level="staff">
                      <Navigate to="/admin/keys" replace />
                    </RouteAccessBoundary>
                  }
                />
                <Route
          path="/governor-registry"
          element={
            <RouteAccessBoundary level="command-center">
              <Navigate to="/governor-command-center" replace />
            </RouteAccessBoundary>
          }
        />
        <Route
          path="/governor-command-center"
          element={
            <RouteAccessBoundary level="command-center">
              <GovernorCommandCenter />
            </RouteAccessBoundary>
          }
        />
          <Route
          path="/governor-command"
          element={
            <RouteAccessBoundary level="command-center">
              <Navigate to="/governor-command-center" replace />
            </RouteAccessBoundary>
          }
        />
        <Route
          path="/governor-executive"
          element={
            <RouteAccessBoundary level="command-center">
              <Navigate to="/governor-command-center" replace />
            </RouteAccessBoundary>
          }
        />
        <Route path="*" element={wrap('NotFound', <NotFound />)} />


              </Routes>

            </MembershipProvider>
          </LanguageProvider>
        </BrowserRouter>

      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
